import { useCallback, useEffect, useId, useRef, useState, useMemo } from "react";
import { Info } from "lucide-react";
import type { LandingCategory } from "@/api/landingContent";
import { Skeleton } from "@/components/ui";
import { runtimeEnv } from "@penn/web-common";

import pemserterImg from "@/assets/images/brand-logo/pemserter.png";
import profilImg from "@/assets/images/brand-logo/profil.png";
import profilFeederImg from "@/assets/images/brand-logo/ProfilFeeder.png";
import robotImg from "@/assets/images/brand-logo/Robot.png";
import robotCellImg from "@/assets/images/brand-logo/Robotcell (2).png";

const requiredBaseUrl = (name: string, value: string | undefined): string => {
  const normalized = value?.trim().replace(/\/+$/, "");
  if (!normalized) {
    throw new Error(
      `Frontend config missing — set ${name} for this deployment.`,
    );
  }
  return normalized;
};

const MEDIA_BASE_URL = requiredBaseUrl(
  "VITE_MEDIA_BASE_URL",
  runtimeEnv("VITE_MEDIA_BASE_URL"),
);
const PUBLIC_ASSETS_BASE_URL = requiredBaseUrl(
  "VITE_PUBLIC_ASSETS_BASE_URL",
  runtimeEnv("VITE_PUBLIC_ASSETS_BASE_URL"),
);

/** Resolve a media (video) URL. Relative keys like "media/Handtools.mp4" get
 * the CDN/S3 host from VITE_MEDIA_BASE_URL — the host is NEVER hardcoded. */
const resolveMediaUrl = (url: string): string => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  // Admin-uploaded hero videos live in the public-assets bucket (like landing
  // images), not the SPA/media bucket that serves the bundled fallback clips.
  if (url.startsWith("landing-videos/")) {
    return `${PUBLIC_ASSETS_BASE_URL}/${url.replace(/^\/+/, "")}`;
  }
  return `${MEDIA_BASE_URL}/${url.replace(/^\/+/, "")}`;
};

/** Known POC product-image relative paths → bundled local assets (these ship
 * with the app, not behind the API), so the detail panel matches the POC. */
const LOCAL_ASSET_MAP: Record<string, string> = {
  "/assets/images/robot.png": robotImg,
  "/assets/images/robotcell (2).png": robotCellImg,
  "/assets/images/robotcell%20(2).png": robotCellImg,
};

/** Resolve a detail image URL. Absolute → as-is; a known POC relative asset →
 * bundled local asset; otherwise prepend the media base. */
const resolveImageUrl = (url: string): string => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const mapped = LOCAL_ASSET_MAP[url.toLowerCase()];
  if (mapped) return mapped;
  // Uploaded landing images live in the public-assets bucket, not the SPA/media bucket.
  return `${PUBLIC_ASSETS_BASE_URL}/${url.replace(/^\/+/, "")}`;
};

type ImageItem = {
  type: string;
  sortOrder: number;
  productId: string | null;
  imageUrl: string;
  caption: string;
};

const mkImage = (imageUrl: string, caption = ""): ImageItem => ({
  type: "",
  sortOrder: 0,
  productId: null,
  imageUrl,
  caption,
});

type Category = {
  id: string;
  title: string;
  subtitle: string;
  /** MICS-163 category definition (`landing_categories.tip`). */
  tip?: string;
  img: string;
  video: string;
  detail: {
    tagline: string;
    description: string[];
    bullets: string[];
    images: ImageItem[];
  };
};

/** Fallback video relative key per category key — used only when the API row
 * has no video_url yet. Final host comes from VITE_MEDIA_BASE_URL. */
const CATEGORY_VIDEOS: Record<string, string> = {
  Custom: "/media/custom.mp4",
  Handtools: "/media/Handtools.mp4",
  InDie: "/media/InDie.mp4",
  "Robotic Integration": "/media/robotic.mp4",
  "Standalone Machines": "/media/Standalone.mp4",
};

/** Fallback image per category key (used when API image URL is relative or unavailable). */
const CATEGORY_FALLBACK_IMGS: Record<string, string> = {
  Custom: robotCellImg,
  Handtools: pemserterImg,
  InDie: profilImg,
  "Robotic Integration": robotImg,
  "Standalone Machines": profilFeederImg,
};

const POC_ORDER = ["handtools", "standalone", "robotic", "indie", "custom"];
const pocOrderRank = (key: string): number => {
  const k = key.toLowerCase();
  const idx = POC_ORDER.findIndex((p) => k.startsWith(p));
  return idx === -1 ? POC_ORDER.length : idx;
};

/** Map one API category to the internal Category shape. */
const fromApiCategory = (api: LandingCategory): Category => {
  const sorted = <T extends { sortOrder: number }>(arr: T[]) =>
    [...arr].sort((a, b) => a.sortOrder - b.sortOrder);

  const paras = sorted(api.paragraphs).map((p) => p.paragraphText);
  const bullets = sorted(api.bullets).map((b) => b.bulletText);
  const images: ImageItem[] = sorted(api.images).map((i) => ({
    type: i.categoryKey,
    sortOrder: i.sortOrder,
    productId: i.productId,
    imageUrl: resolveImageUrl(i.imageUrl),
    caption: i.caption,
  }));

  // Prefer the DB video_url; fall back to the bundled per-category clip. Either
  // way the host is supplied by VITE_MEDIA_BASE_URL (never hardcoded).
  const videoKey = api.videoUrl ?? CATEGORY_VIDEOS[api.categoryKey] ?? "";
  const fallbackImg = CATEGORY_FALLBACK_IMGS[api.categoryKey] ?? pemserterImg;

  return {
    id: api.categoryKey,
    title: api.title,
    subtitle: api.subtitle,
    tip: api.tip || undefined,
    img: images[0]?.imageUrl ?? fallbackImg,
    video: resolveMediaUrl(videoKey),
    detail: {
      tagline: paras[0] ?? "",
      description: paras.slice(1),
      bullets,
      images: images.length > 0 ? images : [mkImage(fallbackImg)],
    },
  };
};

export const HeroSection = ({
  apiCategories,
  isLoading,
}: {
  apiCategories?: LandingCategory[];
  isLoading?: boolean;
}) => {
  const [openId, setOpenId] = useState<string | null>(null);

  const resolvedCategories = useMemo<Category[]>(() => {
    if (!apiCategories) return [];
    return [...apiCategories]
      .sort((a, b) => pocOrderRank(a.categoryKey) - pocOrderRank(b.categoryKey))
      .map(fromApiCategory);
  }, [apiCategories]);

  const handleClick = (id: string) =>
    setOpenId((prev) => (prev === id ? null : id));

  if (isLoading && resolvedCategories.length === 0) {
    return (
      <section className="w-full flex-1 min-h-0 flex flex-col">
        <div className="relative py-4 sm:py-5 flex-1 min-h-0 w-full flex flex-col md:flex-row px-3 sm:px-6 gap-3">
          {Array.from({ length: POC_ORDER.length }).map((_, i) => (
            <Skeleton
              key={i}
              className="basis-0 grow h-full w-full"
              rounded="lg"
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="w-full flex-1 min-h-0 flex flex-col">
      {/* Panel grid — flex-1 fills whatever viewport height remains under the
          fixed header/brand bar (no leftover page space below the tiles). */}
      <div className="relative py-4 sm:py-5 flex-1 min-h-0 w-full flex flex-col md:flex-row px-3 sm:px-6 gap-x-3">
        {resolvedCategories.map((cat) => (
          <LandingPanel
            key={cat.id}
            category={cat}
            isOpen={openId === cat.id}
            anyOpen={openId !== null}
            onClick={() => handleClick(cat.id)}
            onClose={() => setOpenId(null)}
          />
        ))}
      </div>
    </section>
  );
};

/* ── Panel ── */
type LandingPanelProps = {
  category: Category;
  isOpen: boolean;
  anyOpen: boolean;
  onClick: () => void;
  onClose: () => void;
};

const LandingPanel = ({
  category,
  isOpen,
  anyOpen,
  onClick,
  onClose,
}: LandingPanelProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const { title, subtitle, tip, img, video, detail } = category;
  const tipId = useId();
  // Split before the title's last word so the info cue never wraps onto a line by itself.
  const titleBreak = title.lastIndexOf(" ") + 1;

  const close = useCallback(() => {
    onClose();
    tileRef.current?.focus();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close]);

  // The open card plays without hover (touch has none); closing pauses it in
  // place unless a mouse is still over the tile. Live :hover, not enter/leave
  // events: those are lost when the hovered collapsed label unmounts on open,
  // and a tap leaves :hover stuck, hence the hover-capable check.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isOpen) v.play().catch(() => {});
    else if (
      !window.matchMedia("(hover: hover)").matches ||
      !tileRef.current?.matches(":hover")
    )
      v.pause();
  }, [isOpen]);

  const handleMouseEnter = () => {
    videoRef.current?.play().catch(() => {});
  };

  const handleMouseLeave = () => {
    const v = videoRef.current;
    if (v && !isOpen) {
      v.pause();
    }
  };

  const collapsed = anyOpen && !isOpen;

  // Single element whose flex sizing transitions, so the clicked tile grows in
  // place and neighbors compress on their own side (book-opening), instead of
  // swapping render branches that snap with no transition.
  const sizing = isOpen
    ? "grow-[20] min-h-0"
    : collapsed
      ? "grow-0 min-h-10 md:min-h-0 md:min-w-14 lg:min-w-16"
      : "grow min-h-0";

  return (
    <div
      ref={tileRef}
      // Not a button while open: the overlay inside holds its own Close button.
      role={isOpen ? undefined : "button"}
      tabIndex={isOpen ? -1 : 0}
      aria-expanded={isOpen ? undefined : false}
      aria-describedby={tip && !isOpen ? tipId : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (isOpen || e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`group relative basis-0 overflow-hidden cursor-pointer border-b md:border-b-0 md:border-r border-white/10 last:border-0 transition-all duration-500 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-penn-500 ${sizing}`}
    >
      {/* Video layer */}
      <video
        ref={videoRef}
        src={video}
        muted
        loop
        playsInline
        preload="metadata"
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${
          isOpen
            ? "grayscale-0 opacity-80"
            : "grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-80"
        }`}
      />
      {/* Fallback image while video loads */}
      <img
        src={img}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover -z-10 grayscale opacity-60"
      />

      {/* Gradient */}
      <div className="absolute inset-0 bg-linear-to-t from-white/70 via-white/20  to-black/10" />

      {/* Label — rotated strip title when collapsed, bottom label otherwise */}
      {collapsed ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <span
            className="text-3xl font-semibold whitespace-nowrap hidden md:block text-black"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            {title}
          </span>
          <span className="text-caption text-black md:hidden">
            {title}
          </span>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-end px-3 py-5 z-10 justify-center">
          <div className="relative w-full">
            <div className="text-h3 md:text-h2 text-black text-center">
              {title.slice(0, titleBreak)}
              <span className="whitespace-nowrap">
                {title.slice(titleBreak)}
                {/* MICS-163: the info cue reveals the category definition on hover
                    (and on tile keyboard focus); touch users get it in the popup. */}
                {tip && (
                  <span className="group/tip hidden lg:inline-flex align-middle ml-1.5 p-1 -m-1 cursor-help">
                    <Info aria-hidden className="h-[18px] w-[18px] text-penn-700" />
                    <span
                      id={tipId}
                      aria-hidden
                      className="pointer-events-none absolute inset-x-0 bottom-full mb-3 whitespace-normal rounded-xl bg-white/95 px-4 py-3 text-left text-body text-blue_grey1 shadow-soft opacity-0 translate-y-1 transition duration-200 motion-reduce:transition-none group-hover/tip:opacity-100 group-hover/tip:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0"
                    >
                      {tip}
                    </span>
                  </span>
                )}
              </span>
            </div>
            {/* One line on every tile: sized to the tile; hidden where one line cannot fit. */}
            <div className="hidden xl:block truncate text-center text-small text-black">
              {subtitle}
            </div>
          </div>
        </div>
      )}

      {/* Detail overlay (when open) */}
      {isOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-x-4 inset-y-4 md:inset-x-8 md:inset-y-6 z-20 bg-white backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden flex flex-col cursor-auto"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 h-9 w-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-penn-500"
          >
            ×
          </button>
          <div className="p-6 md:p-8 flex flex-col flex-1 min-h-0">
            {/* Header */}
            <div className="text-center mb-5 @container">
              <h2 className="text-h3 text-penn-900 px-8">
                {title}
              </h2>
              {/* Body size (14/21), 16/24 where the longest definition still fits one line;
                  balanced if it must wrap. It may use half the card padding to stay on one line.
                  Raw sizes: the named type classes do not take variants. */}
              <p className="mt-1 md:-mx-4 text-[14px] leading-[21px] @[58rem]:text-[16px] @[58rem]:leading-[24px] text-balance text-blue_grey2">
                {tip ?? subtitle}
              </p>
            </div>

            {/* Body */}
            <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0 overflow-y-auto">
              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold text-gray-800 mb-3 leading-snug">
                  {detail.tagline}
                </p>
                {detail.description.map((para, i) => (
                  <p
                    key={i}
                    className="text-[15px] text-gray-600 mb-2.5 leading-relaxed"
                  >
                    {para}
                  </p>
                ))}
                <ul className="mt-3 space-y-1.5">
                  {detail.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[15px] text-gray-600"
                    >
                      <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-gray-700" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Images — stacked vertically so the text column stays wide
                  enough to avoid an internal scrollbar. */}
              <div className="flex flex-row flex-wrap gap-4 shrink-0 items-start justify-center lg:justify-start">
                {detail.images.map((image, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5">
                    <div className="w-40 h-40 sm:w-48 sm:h-48 lg:w-56 lg:h-56 xl:w-70 xl:h-70 rounded-2xl bg-white flex items-center justify-center overflow-hidden">
                      <img
                        src={image.imageUrl}
                        alt={image.caption || `${title} ${i + 1}`}
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    {image.caption && (
                      <span className="text-[13px] text-gray-500 text-center max-w-40 sm:max-w-48 lg:max-w-56">
                        {image.caption}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
